import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../components/api';
import { Preview, print } from 'react-html2pdf';
import { 
  Building2, Users, FileText, Download, Save,
  ChevronDown, XCircle, Loader2, PieChart, Plus,
  Settings, Trash2, MoveUp, MoveDown, BarChart,
  Thermometer, Building, Box, ClipboardCheck,
  ShieldCheck, Percent
} from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// Fonction utilitaire groupBy
const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const group = item[key];
    result[group] = result[group] || [];
    result[group].push(item);
    return result;
  }, {});
};

const CreateRapport = () => {
  // États pour le rapport
  const [reportTitle, setReportTitle] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedDepartement, setSelectedDepartement] = useState('');
  const [selectedCommune, setSelectedCommune] = useState('');
  const [selectedScope, setSelectedScope] = useState('region'); // region, departement, commune
  
  // États pour les données
  const [regions, setRegions] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  // États pour les sections du rapport
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDepartements, setLoadingDepartements] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [error, setError] = useState(null);

  // Configuration des sections disponibles
  const availableSections = {
    stats: {
      title: 'Statistiques générales',
      type: 'stats',
      component: 'StatCards'
    },
    employeesChart: {
      title: 'Répartition des employés',
      type: 'chart',
      component: 'DonutChart'
    },
    depotsChart: {
      title: 'Conformité des dépôts',
      type: 'chart',
      component: 'DonutChart'
    },
    infrastructureChart: {
      title: 'Infrastructure',
      type: 'chart',
      component: 'DonutChart'
    }
  };

  // Configuration des couleurs pour les graphiques
  const CHART_COLORS = {
    primary: ['#10B981', '#6366F1'],
    secondary: ['#F59E0B', '#EC4899'],
    tertiary: ['#8B5CF6', '#EF4444'],
    quaternary: ['#14B8A6', '#F97316']
  };

  // Liste enrichie des statistiques disponibles basée sur les modèles
  const availableStats = [
    // Statistiques générales
    {
      id: 'total_depots',
      title: 'Total des dépôts',
      category: 'general',
      icon: Building2,
      color: 'from-violet-500 to-violet-700',
      getValue: (stats) => ({
        count: stats.total_depots
      })
    },
    // Ressources Humaines
    {
      id: 'employes',
      title: 'Employés',
      category: 'rh',
      icon: Users,
      color: 'from-blue-500 to-blue-700',
      getValue: (stats) => ({
        count: stats.ressources_humaines.total_agents,
        subtitle: `${stats.ressources_humaines.agents_permanents} permanents`
      })
    },
    // Infrastructure
    {
      id: 'climatisation',
      title: 'Climatisation',
      category: 'infrastructure',
      icon: Thermometer,
      color: 'from-emerald-500 to-emerald-700',
      getValue: (stats) => ({
        count: stats.infrastructure.climatisation.total,
        percentage: stats.infrastructure.climatisation.pourcentage
      })
    },
    // Conformité
    {
      id: 'supervision',
      title: 'Supervision',
      category: 'conformite',
      icon: ClipboardCheck,
      color: 'from-orange-500 to-orange-700',
      getValue: (stats) => ({
        count: stats.conformite.supervision.total,
        percentage: stats.conformite.supervision.pourcentage
      })
    },
    // Gestion des stocks
    {
      id: 'gestion_stock',
      title: 'Gestion des stocks',
      category: 'stock',
      icon: Box,
      color: 'from-pink-500 to-pink-700',
      getValue: (stats) => ({
        count: stats.gestion_stock.systeme_gestion.total,
        percentage: stats.gestion_stock.systeme_gestion.pourcentage
      })
    },
    // Normes et procédures
    {
      id: 'normes',
      title: 'Respect des normes',
      category: 'normes',
      icon: ShieldCheck,
      color: 'from-indigo-500 to-indigo-700',
      getValue: (stats) => ({
        count: stats.normes.application_arrete.total,
        percentage: stats.normes.application_arrete.pourcentage
      })
    }
  ];

  // Liste enrichie des graphiques disponibles
  const availableCharts = [
    {
      id: 'employeesChart',
      title: 'Répartition des employés',
      category: 'rh',
      type: 'donut',
      getData: (stats) => ({
        data: [
          { name: 'Permanents', value: stats.ressources_humaines.agents_permanents },
          { name: 'Temporaires', value: stats.ressources_humaines.total_agents - stats.ressources_humaines.agents_permanents }
        ],
        colors: ['#10B981', '#6366F1']
      })
    },
    {
      id: 'clientsChart',
      title: 'Répartition des clients',
      category: 'clients',
      type: 'donut',
      getData: (stats) => ({
        data: [
          { name: 'Particuliers', value: stats.clients.clients_physiques },
          { name: 'Centres de santé', value: stats.clients.centres_sante },
          { name: 'Dépôts', value: stats.clients.depots_pharmaceutiques },
          { name: 'Hôpitaux', value: stats.clients.hopitaux },
          { name: 'Cliniques', value: stats.clients.cliniques },
          { name: 'Officines', value: stats.clients.officines }
        ],
        colors: ['#10B981', '#6366F1', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6']
      })
    }
  ];

  // Charger les données initiales
  useEffect(() => {
    fetchRegions();
  }, []);

  // Charger les départements quand une région est sélectionnée
  useEffect(() => {
    if (selectedRegion) {
      fetchDepartements();
    }
  }, [selectedRegion]);

  // Charger les communes quand un département est sélectionné
  useEffect(() => {
    if (selectedDepartement) {
      fetchCommunes();
    }
  }, [selectedDepartement]);

  // Charger les statistiques quand le scope change
  useEffect(() => {
    if (shouldFetchStats()) {
      fetchStats();
    }
  }, [selectedRegion, selectedDepartement, selectedCommune, selectedScope]);

  // Fonctions utilitaires
  const shouldFetchStats = () => {
    switch (selectedScope) {
      case 'region':
        return !!selectedRegion;
      case 'departement':
        return !!selectedDepartement;
      case 'commune':
        return !!selectedCommune;
      default:
        return false;
    }
  };

  // Gestionnaires d'événements
  const handleAddSection = (type, item) => {
    setSections([...sections, {
      id: Date.now(),
      type,
      itemId: item.id,
      title: item.title
    }]);
  };

  const handleRemoveSection = (sectionId) => {
    setSections(sections.filter(section => section.id !== sectionId));
  };

  const handleMoveSection = (index, direction) => {
    const newSections = [...sections];
    const temp = newSections[index];
    newSections[index] = newSections[index + direction];
    newSections[index + direction] = temp;
    setSections(newSections);
  };

  // Fonction de sauvegarde et export PDF
  const handleExportPDF = async () => {
    setLoading(true);
    try {
      // Sauvegarder d'abord le rapport
      const reportData = {
        title: reportTitle,
        scope: selectedScope,
        region: selectedRegion,
        departement: selectedDepartement,
        commune: selectedCommune,
        sections: sections,
        stats: stats
      };

      await api.post('/api/reports/create/', reportData);

      // Générer le PDF
      print('report-to-print', reportTitle);
      
    } catch (error) {
      setError("Erreur lors de la sauvegarde ou de l'export du rapport");
      console.error(error);
    }
    setLoading(false);
  };

  // Fonctions de chargement des données
  const fetchRegions = async () => {
    try {
      setError(null);
      const response = await api.get('/collect/regions/');
      setRegions(response.data);
    } catch (error) {
      setError('Erreur lors du chargement des régions');
      console.error(error);
    }
  };

  const fetchDepartements = async () => {
    if (!selectedRegion) return;
    try {
      setLoadingDepartements(true);
      setError(null);
      const response = await api.get(`/collect/departements-by-region/${selectedRegion}/`);
      setDepartements(response.data);
    } catch (error) {
      setError('Erreur lors du chargement des départements');
      console.error(error);
    } finally {
      setLoadingDepartements(false);
    }
  };

  const fetchCommunes = async () => {
    if (!selectedDepartement) return;
    try {
      setLoadingCommunes(true);
      setError(null);
      const response = await api.get(`/collect/communes-by-departement/${selectedDepartement}/`);
      setCommunes(response.data);
    } catch (error) {
      setError('Erreur lors du chargement des communes');
      console.error(error);
    } finally {
      setLoadingCommunes(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      let endpoint;
      let id;

      switch (selectedScope) {
        case 'region':
          endpoint = '/collect/stats-region/';
          id = selectedRegion;
          break;
        case 'departement':
          endpoint = '/collect/stats-departement/';
          id = selectedDepartement;
          break;
        case 'commune':
          endpoint = '/collect/stats-commune/';
          id = selectedCommune;
          break;
      }

      if (id) {
        const response = await api.get(`${endpoint}${id}/`);
        setStats(response.data);
      }
    } catch (error) {
      setError('Erreur lors du chargement des statistiques');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Composant StatCard
  const StatCard = ({ title, count, percentage, icon: Icon, bgColor, subtitle }) => (
    <div className={`${bgColor} rounded-lg shadow-sm p-4 text-white h-24 w-48 flex-shrink-0`}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-medium opacity-90 text-xs mb-1">{title}</h3>
          <p className="text-lg font-bold">
            {count !== undefined ? count.toLocaleString() : '0'}
            {percentage !== undefined && ` (${percentage}%)`}
          </p>
          {subtitle && (
            <p className="text-xs mt-1 opacity-80">{subtitle}</p>
          )}
        </div>
        <div className="bg-white/20 p-1.5 rounded-lg">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );

  // Composant pour les cartes de statistiques
  const StatCards = ({ stats }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        title="Total des dépôts"
        count={stats.total_depots}
        icon={Building2}
        bgColor="bg-gradient-to-br from-violet-500 to-violet-700"
      />
      <StatCard
        title="Employés"
        count={stats.ressources_humaines.total_employes}
        icon={Users}
        bgColor="bg-gradient-to-br from-blue-500 to-blue-700"
        subtitle={`${stats.ressources_humaines.moyenne_employes} en moyenne`}
      />
      {/* ... autres cartes ... */}
    </div>
  );

  // Composant pour les graphiques en donut
  const DonutChart = ({ data, title, colors }) => {
    if (!data || data.length === 0) return null;

    return (
      <div className="bg-white p-4 rounded-lg shadow-sm w-full max-w-md">
        <h3 className="text-sm font-medium text-gray-900 mb-4">{title}</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={data}
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                nameKey="name"
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={colors[index % colors.length]} 
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value.toLocaleString()}`, name]}
              />
              <Legend verticalAlign="bottom" height={36} />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Fonction pour obtenir les données du graphique
  const getChartData = (type) => {
    if (!stats) return { data: [], colors: [] };

    switch (type) {
      case 'employeesChart':
        return {
          data: [
            { name: 'Permanents', value: stats.ressources_humaines.agents_permanents },
            { name: 'Temporaires', value: stats.ressources_humaines.total_agents - stats.ressources_humaines.agents_permanents }
          ],
          colors: CHART_COLORS.primary
        };
      case 'clientsChart':
        return {
          data: [
            { name: 'Particuliers', value: stats.clients.clients_physiques },
            { name: 'Centres de santé', value: stats.clients.centres_sante },
            { name: 'Dépôts', value: stats.clients.depots_pharmaceutiques },
            { name: 'Hôpitaux', value: stats.clients.hopitaux },
            { name: 'Cliniques', value: stats.clients.cliniques },
            { name: 'Officines', value: stats.clients.officines }
          ],
          colors: ['#10B981', '#6366F1', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6']
        };
      case 'depotsChart':
        return {
          data: [
            { name: 'Reconnus', value: stats.depots_reconnus },
            { name: 'Non reconnus', value: stats.total_depots - stats.depots_reconnus }
          ],
          colors: CHART_COLORS.secondary
        };
      case 'infrastructureChart':
        return {
          data: [
            { name: 'Avec climatisation', value: stats.infrastructure.climatisation.total },
            { name: 'Sans climatisation', value: stats.total_depots - stats.infrastructure.climatisation.total }
          ],
          colors: CHART_COLORS.tertiary
        };
      default:
        return { data: [], colors: [] };
    }
  };

  // Fonction de rendu des sections
  const renderSection = (section, stats) => {
    if (!stats) return null;

    if (section.type === 'stat') {
      const stat = availableStats.find(s => s.id === section.itemId);
      if (!stat) return null;
      
      try {
        const value = stat.getValue(stats);
        return (
          <div className="grid grid-cols-6 gap-4">
            <StatCard
              title={stat.title}
              count={value?.count}
              percentage={value?.percentage}
              subtitle={value?.subtitle}
              icon={stat.icon}
              bgColor={`bg-gradient-to-br ${stat.color}`}
            />
          </div>
        );
      } catch (error) {
        console.error('Error rendering stat:', error);
        return null;
      }
    }

    if (section.type === 'chart') {
      const chart = availableCharts.find(c => c.id === section.itemId);
      if (!chart) return null;
      
      try {
        const { data, colors } = chart.getData(stats);
        return (
          <div className="grid grid-cols-12 gap-4">
            <DonutChart
              data={data}
              colors={colors}
              title={chart.title}
            />
          </div>
        );
      } catch (error) {
        console.error('Error rendering chart:', error);
        return null;
      }
    }

    return null;
  };

  // Composant amélioré pour le menu d'ajout de section
  const AddSectionMenu = () => (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Statistiques</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(groupBy(availableStats, 'category')).map(([category, stats]) => (
              <div key={category} className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 capitalize">{category}</h4>
                {stats.map(stat => (
                  <button
                    key={stat.id}
                    onClick={() => handleAddSection('stat', stat)}
                    className="w-full flex items-center p-2 text-left rounded-lg border border-gray-200 hover:bg-gray-50"
                  >
                    <stat.icon className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="text-sm">{stat.title}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Graphiques</h3>
          <div className="grid grid-cols-2 gap-3">
            {availableCharts.map(chart => (
              <button
                key={chart.id}
                onClick={() => handleAddSection('chart', chart)}
                className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                <PieChart className="h-5 w-5 mr-2 text-gray-500" />
                <span className="text-sm">{chart.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Gestion conditionnelle des sélecteurs selon la portée
  const renderLocationSelectors = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {selectedScope !== 'commune' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Région
            </label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
              disabled={loading}
            >
              <option value="">Sélectionner une région</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>{region.nom}</option>
              ))}
            </select>
          </div>
        )}

        {selectedScope !== 'region' && selectedRegion && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Département
            </label>
            <select
              value={selectedDepartement}
              onChange={(e) => setSelectedDepartement(e.target.value)}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
              disabled={loadingDepartements}
            >
              <option value="">Sélectionner un département</option>
              {departements.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.nom}</option>
              ))}
            </select>
          </div>
        )}

        {selectedScope === 'commune' && selectedDepartement && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Commune
            </label>
            <select
              value={selectedCommune}
              onChange={(e) => setSelectedCommune(e.target.value)}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
              disabled={loadingCommunes}
            >
              <option value="">Sélectionner une commune</option>
              {communes.map(commune => (
                <option key={commune.id} value={commune.id}>{commune.nom}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* En-tête du rapport */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du rapport
                </label>
                <input
                  type="text"
                  value={reportTitle}
                  onChange={(e) => setReportTitle(e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
                  placeholder="Rapport d'analyse des dépôts pharmaceutiques"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portée du rapport
                </label>
                <select
                  value={selectedScope}
                  onChange={(e) => {
                    setSelectedScope(e.target.value);
                    setSelectedDepartement('');
                    setSelectedCommune('');
                  }}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
                >
                  <option value="region">Région</option>
                  <option value="departement">Département</option>
                  <option value="commune">Commune</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sélecteurs de zone */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            {renderLocationSelectors()}
          </div>

          {/* Contenu du rapport */}
          <div className="space-y-4 mb-6">
            {sections.map((section, index) => (
              <div key={section.id} className="bg-white rounded-xl shadow-sm">
                <div className="p-3 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-900">{section.title}</h3>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleMoveSection(index, -1)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    >
                      <MoveUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleMoveSection(index, 1)}
                      disabled={index === sections.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    >
                      <MoveDown className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveSection(section.id)}
                      className="p-1 text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {section.type === 'stat' ? (
                    <div className="flex flex-wrap gap-4">
                      {renderSection(section, stats)}
                    </div>
                  ) : (
                    <div className="flex justify-center">
                      {renderSection(section, stats)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Barre d'actions */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex justify-between items-center">
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une section
                </button>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleExportPDF}
                  disabled={loading || !stats || sections.length === 0}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Exporter en PDF
                </button>
              </div>
            </div>
          </div>

          {/* Menu d'ajout de section */}
          {showAddMenu && (
            <div className="mt-6">
              <AddSectionMenu />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CreateRapport;
